import { useEffect, useState } from 'react'

/**
 * The shell's responsive bands — the app's first responsive JavaScript.
 *
 * Everything else in the shell is responsive in CSS alone. The Ask dock cannot
 * be: in the middle band a drawer opening has to *collapse* the dock and
 * closing it has to restore what the merchant had (expanded or collapsed, and
 * the width they dragged to). A media query can hide a column; it cannot
 * remember a preference and hand it back. So the band is decided here, in JS,
 * and the dock's layout state is derived from it.
 *
 * That also makes it testable. `src/test/setup.ts` hard-wires `window.matchMedia`
 * to return `matches:false` for every query with no-op listener methods, so a
 * CSS-only collapse is not assertable anywhere in this repo — nothing in jsdom
 * computes layout, and the global stub never changes its answer. A test drives
 * this hook by replacing `window.matchMedia` with a stub that answers from a
 * width it controls and calls the registered `change` listeners.
 *
 *   `wide`   ≥1600px — the dock and an open drawer coexist; a drawer never
 *                      moves the dock.
 *   `mid`    900–1599 — the dock is persistent, but a drawer opening collapses
 *                      it to its 60px rail, and closing restores the merchant's
 *                      prior state.
 *   `narrow` <900px  — the dock is an overlay (`position:fixed`), the drawer
 *                      keeps full width, and a drawer does not collapse it.
 */
export type Breakpoint = 'narrow' | 'mid' | 'wide'

/** ≥ this, the dock and a drawer coexist. */
export const DOCK_COEXIST_MIN = 1600
/** ≥ this, the dock is a grid column rather than an overlay. */
export const DOCK_PERSISTENT_MIN = 900

export const WIDE_QUERY = `(min-width: ${DOCK_COEXIST_MIN}px)`
export const MID_QUERY = `(min-width: ${DOCK_PERSISTENT_MIN}px)`

/**
 * `window.matchMedia`, or `undefined` where there is none.
 *
 * Absent entirely — SSR, a jsdom run whose setup file did not load, an old
 * embedded webview — is NOT the same as "every query is false". All-false is a
 * real answer and means a viewport under 900px; no `matchMedia` at all means we
 * do not know, and `readBand` answers `wide` for it, because `wide` is the band
 * in which nothing automatic happens to the dock. Guessing `narrow` there would
 * make the dock a fixed overlay on a desktop that simply could not be measured.
 */
function media(): ((query: string) => MediaQueryList) | undefined {
  if (typeof window === 'undefined') return undefined
  const mm = window.matchMedia
  return typeof mm === 'function' ? (query: string) => mm.call(window, query) : undefined
}

/** The band right now, read from `matchMedia` rather than from `innerWidth`. */
export function readBand(): Breakpoint {
  const mm = media()
  if (!mm) return 'wide'
  if (mm(WIDE_QUERY).matches) return 'wide'
  if (mm(MID_QUERY).matches) return 'mid'
  return 'narrow'
}

/**
 * The current band, re-read whenever either query changes.
 *
 * Subscribes with `addEventListener('change')` and falls back to the deprecated
 * `addListener` only where the former is absent (Safari < 14, and any stub that
 * predates `MediaQueryList` being an `EventTarget`). A stub offering neither is
 * read once and never updates — which is exactly what the global test setup
 * does, and why it reports `narrow` and stays there rather than crashing.
 */
export function useBreakpoint(): Breakpoint {
  const [band, setBand] = useState<Breakpoint>(readBand)

  useEffect(() => {
    const mm = media()
    if (!mm) return

    const sync = () => setBand(readBand())
    // The viewport can move between the first render and this effect — and a
    // test that stubs `matchMedia` before rendering but after the module loaded
    // needs the mount itself to read, not only a later `change`.
    sync()

    const detach = [WIDE_QUERY, MID_QUERY].map((query) => {
      const list = mm(query)
      if (typeof list.addEventListener === 'function') {
        list.addEventListener('change', sync)
        return () => list.removeEventListener('change', sync)
      }
      if (typeof list.addListener === 'function') {
        list.addListener(sync)
        return () => list.removeListener?.(sync)
      }
      return () => {}
    })

    return () => detach.forEach((off) => off())
  }, [])

  return band
}

/**
 * The viewport's width in CSS pixels, tracked across resizes.
 *
 * ⚠️ Separate from `useBreakpoint`, and it has to be: a band is a question
 * `matchMedia` can answer, but "how much room is left for the dock once the
 * rail and the context panel have taken theirs" is a *number*, and no media
 * query yields one.
 *
 * ⚠️ Testing this needs a real dispatch. `src/test/setup.ts` replaces
 * `window.dispatchEvent` with a `vi.fn()`, so `fireEvent.resize(window)` calls
 * a stub and no listener runs; a test drives it with
 * `EventTarget.prototype.dispatchEvent.call(window, new Event('resize'))`,
 * which bypasses the mock. `src/test/match-media.ts` has the helper.
 */
export function useViewportWidth(): number {
  const read = () => (typeof window === 'undefined' ? 0 : window.innerWidth || 0)
  const [width, setWidth] = useState(read)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sync = () => setWidth(read())
    // The window can resize between the first render and this effect.
    sync()
    window.addEventListener('resize', sync)
    return () => window.removeEventListener('resize', sync)
  }, [])

  return width
}
